import { Button, Pagination, Stack, Table, TableBody, TableHeader, TableRow } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useNdiCreditsBalance, useNdiCreditsTransactions } from '@/hooks/useNdiCredits';
import { useOrganization } from '@/hooks/useOrganization';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import {
  StaffPortalTableCell,
  StaffPortalTableEmptyRow,
  StaffPortalTableHeadCell,
} from '@/pages/shared/staff-portal/components/StaffPortalTable';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { AddCreditsDialog } from '../components/AddCreditsDialog';
import { ManualCostDialog } from '../components/ManualCostDialog';

const PAGE_SIZE = 20;

export const NdiFinancialPage = () => {
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const organization = useOrganization({ id: organizationId }).data.results[0];

  if (isNullish(organizationId) || isNullish(organization)) {
    throw new Error(`Unknown organization with id ${organizationId}`);
  }

  const { data: balance } = useNdiCreditsBalance(organizationId);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [manualCostOpen, setManualCostOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: transactionsData } = useNdiCreditsTransactions(organizationId, {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  });
  const transactions = transactionsData.results;

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      showBreadcrumbs={false}
      navBarProps={{
        title: t('shared.ndiOrganizationSubscriptions.financialPage.title', { name: organization.name }),
        backPath: buildPath(paths.staff.organizationNdiSubscriptions, { pathParams: { organizationId } }),
        showSearchBar: false,
      }}
    >
      <Stack className='px-48 py-12 gap-6'>
        <Stack direction='row' className='items-start justify-between'>
          <Stack spacing={2} className='p-6 rounded-2xl bg-white-100 border border-neutral-black-12 w-fit'>
            <span className='typography-overline text-text-secondary'>
              {t('shared.ndiOrganizationSubscriptions.financialPage.balance')}
            </span>
            <span className='typography-h3'>
              {balance.balance} {balance.currency}
            </span>
          </Stack>
          <Stack direction='row' spacing={2}>
            <Button variant='outline' onClick={() => setManualCostOpen(true)}>
              {t('shared.ndiOrganizationSubscriptions.financialPage.manualCostButton')}
            </Button>
            <Button onClick={() => setAddCreditsOpen(true)}>
              {t('shared.ndiOrganizationSubscriptions.financialPage.addCreditsButton')}
            </Button>
          </Stack>
        </Stack>

        <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
          <Table>
            <TableHeader>
              <TableRow>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.transactionId')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.date')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.description')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.type')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.amount')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.balanceAfter')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.jobSubscription')}
                </StaffPortalTableHeadCell>
                <StaffPortalTableHeadCell>
                  {t('shared.ndiOrganizationSubscriptions.financialPage.fields.subscriptionAccount')}
                </StaffPortalTableHeadCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 && (
                <StaffPortalTableEmptyRow
                  colSpan={8}
                  message={t('shared.ndiOrganizationSubscriptions.financialPage.emptyState')}
                />
              )}
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} className='border-b border-divider'>
                  <StaffPortalTableCell className='font-mono'>
                    <span title={transaction.uuid}>{transaction.uuid.split('-')[0]}</span>
                  </StaffPortalTableCell>
                  <StaffPortalTableCell>{new Date(transaction.date).toLocaleDateString()}</StaffPortalTableCell>
                  <StaffPortalTableCell>{transaction.description}</StaffPortalTableCell>
                  <StaffPortalTableCell className='capitalize'>{transaction.type}</StaffPortalTableCell>
                  <StaffPortalTableCell>
                    {transaction.type === 'debit' ? '+' : '-'}
                    {transaction.amount}
                  </StaffPortalTableCell>
                  <StaffPortalTableCell>{transaction.balance_after}</StaffPortalTableCell>
                  <StaffPortalTableCell>{transaction.job_subscription_display_name ?? '—'}</StaffPortalTableCell>
                  <StaffPortalTableCell>
                    {transaction.subscription_account_name
                      ? `${transaction.subscription_account_name} (${transaction.subscription_account_balance})`
                      : '—'}
                  </StaffPortalTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination
          className='typography-overline text-text-secondary mx-auto'
          currentPage={currentPage}
          totalCount={transactionsData.count}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
        />
      </Stack>
      <AddCreditsDialog
        open={addCreditsOpen}
        onClose={() => setAddCreditsOpen(false)}
        organizationId={organizationId}
      />
      <ManualCostDialog
        open={manualCostOpen}
        onClose={() => setManualCostOpen(false)}
        organizationId={organizationId}
      />
    </SignedInPageLayout>
  );
};
